import { notEmpty } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentHelper } from "scmp-app/components/content/hooks";
import { SCMPLabLogo } from "scmp-app/components/scmp-lab-logo";
import { formatInTimeZone, toISOString } from "scmp-app/lib/utils";
import type { styleArticleAuthorBlockArticle$key } from "scmp-app/queries/__generated__/styleArticleAuthorBlockArticle.graphql";

import {
  ArticleDateContainer,
  AuthorNameContainer,
  AuthorNameContainerLabel,
  AuthorNameWrapper,
  Container,
  SCMPLabLogoContainer,
  SponsorInfoContainer,
  SponsorInfoWrapper,
  SponsorName,
  SponsorType,
  StyledAuthorName,
} from "./styles";

export type Props = {
  className?: string;
  reference: styleArticleAuthorBlockArticle$key;
};

export const StyleArticleAuthorBlock: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const article = useFragment(
    graphql`
      fragment styleArticleAuthorBlockArticle on Article {
        createdDate
        publishedDate
        sponsorType
        firstTopic {
          ... on Topic {
            sponsor {
              name
              type
            }
          }
        }
        authors {
          ...authorNameAuthor
          entityId
        }
        ...hooksContentHelperContent
      }
    `,
    reference_,
  );

  const { isRecapArticleType } = useContentHelper(article);
  if (!article) return null;
  const dateFormat = "MMM d , yyyy";
  const publishedDate = notEmpty(article.publishedDate)
    ? article.publishedDate
    : article.createdDate;

  const renderAuthorsName = () => {
    if (!notEmpty(article.authors)) return null;

    return (
      <AuthorNameContainer $isMultipleAuthor={article.authors.length > 1}>
        <AuthorNameContainerLabel>STORY</AuthorNameContainerLabel>
        {article.authors.map(author => {
          if (!author) return null;

          return (
            <AuthorNameWrapper key={author.entityId}>
              <StyledAuthorName reference={author} />
            </AuthorNameWrapper>
          );
        })}
      </AuthorNameContainer>
    );
  };

  const showSponsorInfo =
    notEmpty(article.sponsorType) &&
    notEmpty(article.firstTopic?.sponsor?.name) &&
    notEmpty(article.firstTopic?.sponsor?.type);

  return (
    <Container className={className}>
      {renderAuthorsName()}
      <ArticleDateContainer $showSponsorInfo={showSponsorInfo}>
        <time dateTime={publishedDate ? toISOString(publishedDate) : undefined}>
          {publishedDate && formatInTimeZone(publishedDate, dateFormat)}
        </time>
      </ArticleDateContainer>
      {isRecapArticleType && (
        <SCMPLabLogoContainer>
          <SCMPLabLogo />
        </SCMPLabLogoContainer>
      )}
      {showSponsorInfo && (
        <SponsorInfoContainer>
          <SponsorInfoWrapper>
            <SponsorType>{article.firstTopic?.sponsor?.type}</SponsorType>
            <SponsorName>{article.firstTopic?.sponsor?.name}</SponsorName>
          </SponsorInfoWrapper>
        </SponsorInfoContainer>
      )}
    </Container>
  );
};
